// The signed-in app's frame (UI kit W1–W5): a dark sidebar on wide screens,
// an icon rail on medium ones and a top bar with a menu on phones. Holds
// the keyboard shortcuts (⌘K or / to search, N for a new invoice), the
// search palette, the offline and new-version bars, and page tracking.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { businessInitials, canAddBusiness as canAdd, FREE_BUSINESS_LIMIT } from '@/core/business';
import { useAppData } from '@/app/AppData';
import { CommandPalette } from '@/app/CommandPalette';
import { ThemeButton } from '@/app/ThemeButton';
import { track } from '@/data/analytics';
import { signOut } from '@/data/authService';
import { t } from '@/l10n/strings';
import { Button, ButtonLink, IconButton, LogoMark, ProBadge } from '@/ui/controls';
import { Icon, type IconName } from '@/ui/Icon';
import { Menu } from '@/ui/Menu';
import { useToast } from '@/ui/Toasts';

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
export const searchShortcut = isMac ? '⌘K' : 'Ctrl K';

/** Whether a key press is going into a text field rather than the page. */
function typingIn(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
}

export function Shell() {
  const { online } = useAppData();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const main = useRef<HTMLElement>(null);

  // A new page: close the drawer and move focus to it, as a page load would.
  useEffect(() => {
    setDrawerOpen(false);
    main.current?.focus({ preventScroll: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const openPalette = useCallback(() => {
    track('palette_opened', { via: 'button' });
    setPaletteOpen(true);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (!paletteOpen) track('palette_opened', { via: 'keyboard' });
        setPaletteOpen((open) => !open);
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey || typingIn(e.target) || paletteOpen || document.querySelector('dialog[open]')) return;
      if (e.key === '/') {
        e.preventDefault();
        track('shortcut_used', { action: 'search' });
        setPaletteOpen(true);
      } else if (e.key === 'n' || e.key === 'N') {
        e.preventDefault();
        track('shortcut_used', { action: 'new_invoice' });
        navigate('/invoices/new');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate, paletteOpen]);

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {t('webSkipToContent')}
      </a>
      <Sidebar open={drawerOpen} onSearch={openPalette} />
      {drawerOpen && <div className="scrim" onClick={() => setDrawerOpen(false)} aria-hidden />}
      <div className="main">
        <header className="topbar">
          <IconButton icon="menu" label={t('webMenu')} onClick={() => setDrawerOpen(true)} aria-expanded={drawerOpen} />
          <Link to="/home" className="topbar__logo">
            <LogoMark size={30} />
            {t('appName')}
          </Link>
          <span className="spacer" />
          <ThemeButton placement="down" />
          <IconButton icon="search" label={t('webSearchHint')} onClick={openPalette} />
          <ButtonLink to="/invoices/new" variant="action" size="sm" icon="plus">
            {t('shellNewInvoice')}
          </ButtonLink>
        </header>
        {!online && (
          <div className="banner banner--info shell-banner" role="status">
            <Icon name="wifioff" />
            <div className="banner__txt">
              <b>{t('offlineTitle')}</b>
              <span>{t('webOfflineMessage')}</span>
            </div>
          </div>
        )}
        <UpdateBar />
        <main id="main" ref={main} tabIndex={-1} style={{ outline: 'none' }}>
          <Outlet />
        </main>
      </div>
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} />}
    </div>
  );
}

function SideLink({ to, icon, label, end }: { to: string; icon: IconName; label: string; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className="sidebar__item" title={label}>
      <Icon name={icon} size={19} />
      <span>{label}</span>
    </NavLink>
  );
}

function Sidebar({ open, onSearch }: { open: boolean; onSearch: () => void }) {
  const { business, businesses = [], setActiveBusiness, user, offersPro, isPro } = useAppData();
  // One extra business on the free plan; more only on a Pro plan.
  const canAddBusiness = canAdd(businesses.length, isPro);
  const navigate = useNavigate();
  const toast = useToast();
  const name = business?.name || t('appName');
  return (
    <nav className="sidebar" data-open={open} aria-label={t('webMainNavigation')}>
      <Link to="/home" className="sidebar__logo">
        <LogoMark size={34} />
        <span>{t('appName')}</span>
      </Link>
      <ButtonLink to="/invoices/new" variant="signal" block className="sidebar__new">
        <Icon name="plus" size={18} stroke={2.4} />
        <span>{t('shellNewInvoice')}</span>
      </ButtonLink>
      <button type="button" className="sidebar__item" onClick={onSearch} style={{ background: 'none', border: 0, width: '100%' }} title={t('webSearchHint')}>
        <Icon name="search" size={19} />
        <span className="grow" style={{ textAlign: 'left' }}>
          {t('webSearch')}
        </span>
        <span className="kbd" style={{ background: 'rgba(255,255,255,0.1)', color: 'inherit', boxShadow: 'none' }}>
          {searchShortcut}
        </span>
      </button>
      <SideLink to="/home" icon="home" label={t('navDashboard')} />
      <SideLink to="/invoices" icon="file" label={t('navInvoices')} />
      <SideLink to="/clients" icon="users" label={t('navClients')} />
      <p className="sidebar__group">{t('webSectionBusiness')}</p>
      <SideLink to="/settings/design" icon="palette" label={t('webTitleDesign')} />
      <SideLink to="/settings" icon="gear" label={t('navSettings')} end />
      <div className="sidebar__foot">
        {offersPro && (
          <div className="sidebar__pro">
            <span className="pro-badge pro-badge--brass" style={{ alignSelf: 'flex-start' }}>
              <Icon name="crown" size={11} stroke={2} />
              {t('commonPro')}
            </span>
            <b style={{ font: '400 22px/1.15 var(--font-serif)' }}>
              {t('shellGetPaid')}
              <em style={{ color: 'var(--pro)' }}>{t('shellGetPaidEnd')}</em>
            </b>
            <span className="small" style={{ color: 'var(--on-surface-strong-muted)', fontSize: 12.5, lineHeight: '17px' }}>
              {t('shellPaymentLinksRemindersAndEvery')}
            </span>
            <Link to="/pro" className="label" style={{ color: 'var(--pro)' }}>
              {t('shellTry7DaysFree')}
            </Link>
          </div>
        )}
        <ThemeButton placement="up" />
        <Menu
          placement="up"
          trigger={(props) => (
            <button type="button" className="sidebar__account" {...props} title={t('webAccount')}>
              <span className="avatar avatar--brand" style={{ background: 'rgba(255,255,255,0.1)' }} aria-hidden>
                {business?.logo ? <img src={business.logo} alt="" /> : businessInitials(name)}
              </span>
              <div className="grow">
                <b>{name}</b>
                <span>{user?.isGuest ? t('webGuest') : (user?.email ?? t('settingsSignedIn'))}</span>
              </div>
            </button>
          )}
        >
          {(close) => (
            <>
              <span className="menu__label">{t('businessesTitle')}</span>
              {businesses.map((b) => (
                <button
                  key={b.id}
                  role="menuitemradio"
                  aria-checked={b.id === business?.id}
                  type="button"
                  className="menu__item"
                  onClick={() => {
                    close();
                    if (b.id === business?.id) return;
                    setActiveBusiness(b.id);
                    navigate('/home');
                    toast(t('businessesNowShowing', { name: b.name.trim() || t('businessesUnnamed') }));
                  }}
                >
                  <span className="avatar avatar--grey" style={{ width: 24, height: 24, borderRadius: 7, fontSize: 10 }} aria-hidden>
                    {b.logo ? <img src={b.logo} alt="" /> : businessInitials(b.name)}
                  </span>
                  <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {b.name.trim() || t('businessesUnnamed')}
                  </span>
                  {b.id === business?.id && <Icon name="check" size={16} />}
                </button>
              ))}
              <button
                role="menuitem"
                type="button"
                className="menu__item"
                onClick={() => {
                  close();
                  if (canAddBusiness) navigate('/setup?add=1');
                  else if (offersPro) navigate('/pro');
                  else toast(t('businessesLimitReached', { count: FREE_BUSINESS_LIMIT }));
                }}
              >
                <Icon name="plus" size={18} />
                <span className="grow">{t('businessesAdd')}</span>
                {!canAddBusiness && <ProBadge />}
              </button>
              <div className="menu__sep" />
              <Link role="menuitem" className="menu__item" to="/settings/profile" onClick={close}>
                <Icon name="store" size={18} />
                {t('webTitleProfile')}
              </Link>
              <Link role="menuitem" className="menu__item" to="/settings" onClick={close}>
                <Icon name="gear" size={18} />
                {t('navSettings')}
              </Link>
              {user?.isGuest && (
                <Link role="menuitem" className="menu__item" to="/sign-in" onClick={close}>
                  <Icon name="cloudup" size={18} />
                  {t('webBackUp')}
                </Link>
              )}
              <a role="menuitem" className="menu__item" href="/support" target="_blank" rel="noreferrer" onClick={close}>
                <Icon name="info" size={18} />
                {t('webHelp')}
              </a>
              {!user?.isGuest && (
                <>
                  <div className="menu__sep" />
                  <button
                    role="menuitem"
                    type="button"
                    className="menu__item"
                    onClick={async () => {
                      close();
                      try {
                        await signOut();
                        navigate('/welcome');
                      } catch {
                        toast(t('authSignInFailed'), { error: true });
                      }
                    }}
                  >
                    <Icon name="logout" size={18} />
                    {t('commonSignOut')}
                  </button>
                </>
              )}
            </>
          )}
        </Menu>
      </div>
    </nav>
  );
}

/** "A new version of Paidlark is ready": checked when the tab comes back into view. */
function UpdateBar() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (import.meta.env.DEV) return;
    let last = 0;
    const check = async () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5 * 60_000) return;
      last = Date.now();
      try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        const { build } = (await response.json()) as { build?: string };
        if (build && build !== __APP_BUILD__) setReady(true);
      } catch {
        // Offline: check again later.
      }
    };
    document.addEventListener('visibilitychange', check);
    return () => document.removeEventListener('visibilitychange', check);
  }, []);
  if (!ready) return null;
  return (
    <div className="banner banner--info shell-banner" role="status">
      <Icon name="repeat" />
      <div className="banner__txt">
        <b>{t('shellANewVersionOfPaidlark')}</b>
      </div>
      <Button variant="action" size="sm" onClick={() => window.location.reload()}>
        {t('shellRefresh')}
      </Button>
    </div>
  );
}
